import type { Stripe } from '@stripe/stripe-js';
// The default '@stripe/stripe-js' entry injects the js.stripe.com script (and
// its iframes) on module import, which puts Stripe in the startup path of every
// page via the payments feature barrel. The 'pure' entry only loads Stripe.js
// when loadStripe() is called, i.e. when a payment surface actually mounts.
// (The type-only import above is erased at compile time — no side effect.)
import { loadStripe } from '@stripe/stripe-js/pure';

const stripeInstanceCache = new Map<string, Promise<Stripe | null>>();

export function getStripeInstance(stripeAccountId?: string | null): Promise<Stripe | null> {
  const publishableKey = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) ?? '';
  if (!publishableKey) {
    return Promise.resolve(null);
  }

  const cacheKey = `${publishableKey}:${stripeAccountId || 'platform'}`;
  const cached = stripeInstanceCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  // Drop rejected loads from the cache so a transient failure (ad blocker
  // toggle, network blip) doesn't poison Stripe for the rest of the tab.
  const loading = loadStripe(
    publishableKey,
    stripeAccountId ? { stripeAccount: stripeAccountId } : undefined,
  ).catch((error: unknown) => {
    stripeInstanceCache.delete(cacheKey);
    throw error;
  });
  stripeInstanceCache.set(cacheKey, loading);
  return loading;
}
